import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import StatusTag from '../../../components/ui/StatusTag';
import Field, { TextInput, NativeSelect } from '../../../components/ui/Field';
import CredentialEditModal, { KEY_RE, agentsText } from './CredentialEditModal';

// Clave de OpenAI del agente. Se guarda en Vault: aquí solo se ven los últimos 4 caracteres. Puede ser una clave PÚBLICA (la comparten
// varios agentes, pensada para pruebas de concepto de ventas), una propia del agente o una guardada sin uso. Cualquiera se puede editar
// (cuando caduca se pega la nueva) y el agente puede cambiar de una a otra cuando quiera.
const MODES = [['public', 'Una pública'], ['own', 'Dar de alta una propia'], ['saved', 'Una ya guardada']];

export default function CredentialPanel({ credential, freeCredentials = [], publicCredentials = [], readOnly = false, onReplace, onUse, onUpdate }) {
  const modes = MODES.filter(([k]) => (k === 'public' ? publicCredentials.length > 0 : k === 'saved' ? freeCredentials.length > 0 : true));
  const [mode, setMode] = useState(publicCredentials.length ? 'public' : 'own');
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [show, setShow] = useState(false);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [editing, setEditing] = useState(false);
  const active = modes.some(([k]) => k === mode) ? mode : 'own';

  const secretError = touched && !KEY_RE.test(secret.trim()) ? 'La clave empieza con «sk-» y no lleva espacios.' : '';
  const labelError = touched && !label.trim() ? 'Ponle un nombre para reconocerla, por ejemplo el del cliente.' : '';

  const save = async () => {
    setTouched(true);
    if (!KEY_RE.test(secret.trim()) || !label.trim()) return;
    setBusy('save'); setError('');
    try { await onReplace({ label, secret, isPublic }); setSecret(''); setLabel(''); setIsPublic(false); setTouched(false); }
    catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const use = async () => {
    if (!pick) return;
    setBusy('use'); setError('');
    try { await onUse(Number(pick)); setPick(''); } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const list = active === 'public' ? publicCredentials : freeCredentials;

  return (
    <div className="max-w-2xl space-y-8">
      <section aria-labelledby="cred-now" className="rounded-xl border border-line p-5">
        <h3 id="cred-now" className="text-[15px] font-semibold text-fg">Clave en uso</h3>
        {credential ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-sm text-fg">{credential.label}</span>
            <span className="text-sm text-fg-dim tabular-nums" aria-label={`termina en ${credential.last4}`}>•••• {credential.last4}</span>
            {credential.status === 'active' ? <StatusTag tone="live" dot>Activa</StatusTag> : <StatusTag tone="caution">OpenAI la rechazó</StatusTag>}
            {credential.is_public && <StatusTag>Pública</StatusTag>}
            {!readOnly && onUpdate && <Btn size="sm" variant="ghost" className="ml-auto" onClick={() => setEditing(true)}>Editar</Btn>}
            {credential.is_public && <p className="basis-full text-[13px] text-fg-dim">{credential.agent_count > 1 ? `La comparten ${agentsText(credential.agent_count)}: si la editas o caduca, cambia para todos.` : 'Es pública: cualquiera del equipo puede asignarla a otros agentes.'}</p>}
            {credential.last_error && <p className="basis-full text-[13px] text-caution">{credential.last_error}</p>}
            {credential.status !== 'active' && onUpdate && !readOnly && <p className="basis-full text-[13px] text-fg-dim">Si la clave caducó, edítala y pega la nueva: se reactiva sola.</p>}
          </div>
        ) : (
          <p className="mt-2 text-sm text-fg-dim">Este agente no tiene clave. Sin ella no puede publicarse ni responder.</p>
        )}
      </section>

      <section aria-labelledby="cred-new" className="space-y-4">
        <h3 id="cred-new" className="text-[15px] font-semibold text-fg">{credential ? 'Cambiar la clave' : 'Elegir una clave'}</h3>
        {modes.length > 1 && (
          <div role="radiogroup" aria-label="Qué clave usar" className="flex flex-wrap rounded-lg border border-line p-0.5 bg-ink w-full sm:w-auto sm:inline-flex">
            {modes.map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={active === k} onClick={() => { setMode(k); setPick(''); setError(''); }} disabled={readOnly}
                className={`flex-1 sm:flex-none h-9 px-3 rounded-md text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${active === k ? 'bg-raised text-fg' : 'text-fg-dim hover:text-fg'}`}>{l}</button>
            ))}
          </div>
        )}

        {active === 'own' ? (
          <>
            <div className="grid sm:grid-cols-[1fr_1.4fr] gap-4">
              <Field label="Nombre de la clave" error={labelError}>
                <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Clínica Demo" maxLength={80} autoComplete="off" disabled={readOnly} />
              </Field>
              <Field label="Clave de OpenAI" error={secretError} hint="Se guarda cifrada. Después nadie puede volver a verla completa."
                adornment={
                  <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? 'Ocultar la clave' : 'Mostrar la clave'}
                    className="h-8 w-8 grid place-items-center rounded-md text-fg-faint hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
                    {show ? <FaEyeSlash aria-hidden /> : <FaEye aria-hidden />}
                  </button>}>
                <TextInput type={show ? 'text' : 'password'} value={secret} onChange={(e) => setSecret(e.target.value)}
                  placeholder="sk-…" autoComplete="off" spellCheck={false} className="pr-10 tabular-nums" disabled={readOnly} />
              </Field>
            </div>
            <label className="flex items-start gap-3 text-[13.5px] cursor-pointer">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} disabled={readOnly}
                className="mt-0.5 h-4 w-4 accent-[rgb(var(--live))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70" />
              <span>
                <span className="font-medium text-fg">Que sea pública</span>
                <span className="block text-[12.5px] text-fg-dim">Cualquiera del equipo podrá asignarla a los agentes de pruebas de concepto. Nadie ve la clave.</span>
              </span>
            </label>
            <div className="flex items-center gap-3">
              <Btn variant="primary" onClick={save} loading={busy === 'save'} disabled={readOnly}>{credential ? 'Cambiar clave' : 'Guardar clave'}</Btn>
              {credential && !credential.is_public && <span className="text-[12.5px] text-fg-faint">La anterior se borra si ningún otro agente la usa.</span>}
              {credential?.is_public && <span className="text-[12.5px] text-fg-faint">La pública sigue disponible para los demás agentes.</span>}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-[13px] text-fg-dim max-w-[62ch]">
              {active === 'public' ? 'Para una prueba de concepto: el agente usa una clave del equipo. Cuando el cliente se quede, cámbiala por una propia.' : 'Claves privadas que ningún agente usa todavía.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <Field label={active === 'public' ? 'Claves públicas' : 'Claves sin agente'} className="flex-1">
                <NativeSelect value={pick} onChange={(e) => setPick(e.target.value)} disabled={readOnly}>
                  <option value="">Elige una clave…</option>
                  {list.map((c) => <option key={c.id} value={c.id}>{c.label} · termina en {c.last4}{c.is_public && c.agent_count ? ` · la usa${c.agent_count === 1 ? '' : 'n'} ${agentsText(c.agent_count)}` : ''}</option>)}
                </NativeSelect>
              </Field>
              <Btn onClick={use} disabled={!pick || readOnly} loading={busy === 'use'}>Usar esta clave</Btn>
            </div>
          </div>
        )}
      </section>
      {error && <p role="alert" className="text-sm text-signal">{error}</p>}
      {onUpdate && <CredentialEditModal open={editing} onClose={() => setEditing(false)} credential={credential} onSave={onUpdate} />}
    </div>
  );
}
